// Pass 5 — /run/matrix/:matrix_job_id
//
// Results grid for a matrix job. Rows = threats, cols = environments.
// (Guidance axis is rendered as tabs when present — keeps the grid
// 2-D so cell click → /analyse/runs/:id stays clean.)
//
// Each cell shows:
//   - State chip (queued / running / complete / failed)
//   - Outcome chip (kill / miss / mixed) once complete
//   - mean miss distance in m
//
// Polling: every 4s while any cell is queued/running, paused once
// the matrix reaches a terminal state. Same axios client; no SSE
// or new transport — sticks to the existing pattern.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Download,
    FileText,
    RefreshCw,
    Trash2,
} from "lucide-react";
import { formatApiError } from "@/lib/formatApiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "@/components/ui/tabs";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import { sim } from "@/api/endpoints";

// Pass 5 — outcome → status-pill key. ``mixed`` doesn't map to a
// run status but uses the ``cancelling`` palette as a neutral
// warning yellow.
const OUTCOME_PILL = {
    kill: "succeeded",
    miss: "failed",
    mixed: "cancelling",
};

const STATE_PILL = {
    queued: "queued",
    running: "running",
    complete: "succeeded",
    failed: "failed",
};

const POLL_MS = 4000;
const TERMINAL = new Set(["complete", "complete_with_failures"]);

function MatrixCell({ cell, onClick }) {
    const stateKey = STATE_PILL[cell.state] || "queued";
    const outcomeKey = OUTCOME_PILL[cell.outcome] || null;
    const miss = cell.miss_mean_m;
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={!cell.run_id}
            data-testid={`matrix-cell-${cell.cell_index}`}
            data-state={cell.state}
            data-outcome={cell.outcome || ""}
            className={[
                "relative text-left p-2.5 rounded-md border min-h-[88px] transition-colors",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-ux-accent",
                cell.run_id
                    ? "border-ux-border-1 bg-ux-bg-1 hover:bg-ux-bg-2 cursor-pointer"
                    : "border-ux-border-1 bg-ux-bg-2/40 cursor-not-allowed",
            ].join(" ")}
        >
            <div className="flex items-center justify-between gap-2 mb-1.5">
                <StatusPill
                    status={stateKey}
                    testId={`matrix-cell-state-${cell.cell_index}`}
                >
                    {cell.state}
                </StatusPill>
                {outcomeKey && (
                    <StatusPill
                        status={outcomeKey}
                        testId={`matrix-cell-outcome-${cell.cell_index}`}
                        className="uppercase tracking-wider text-[10px]"
                    >
                        {cell.outcome}
                    </StatusPill>
                )}
            </div>
            {typeof miss === "number" ? (
                <div className="font-mono text-[12px] text-ux-text-2 tabular-nums">
                    miss&nbsp;{miss.toFixed(2)}&nbsp;m
                </div>
            ) : (
                <div className="font-mono text-[11px] text-ux-text-3">
                    {cell.state === "queued"
                        ? "waiting…"
                        : cell.state === "running"
                          ? "running…"
                          : "—"}
                </div>
            )}
            {cell.run_id && (
                <div className="text-[10px] font-mono text-ux-text-3 mt-1 truncate">
                    {cell.run_id.slice(-12)}
                </div>
            )}
        </button>
    );
}

function MatrixGrid({ matrix, guidanceFilter, onCellClick }) {
    const threatIds = matrix?.axes?.threat_ids || [];
    const envIds = matrix?.axes?.environment_ids || [];
    // Phase 14.8 W5 — wrap `cells` in useMemo so its identity is stable
    // across renders when `matrix.cells` hasn't changed; otherwise the
    // `lookup` Map at line 129 is rebuilt on every render even when the
    // underlying matrix is identical.
    const cells = useMemo(() => matrix?.cells || [], [matrix?.cells]);
    const lookup = useMemo(() => {
        const m = new Map();
        for (const c of cells) {
            const key = `${c.threat_id}|${c.environment_id}|${c.guidance_profile_id}`;
            m.set(key, c);
        }
        return m;
    }, [cells]);
    const guidanceForRow = guidanceFilter || matrix?.axes?.guidance_profile_ids?.[0];
    return (
        <div
            className="overflow-x-auto"
            data-testid="matrix-grid"
        >
            <table className="w-full border-collapse">
                <thead>
                    <tr>
                        <th className="text-left p-2 text-[11px] font-mono text-ux-text-3 uppercase tracking-wider">
                            Threat \ Env
                        </th>
                        {envIds.map((eid) => (
                            <th
                                key={eid}
                                className="text-left p-2 text-[11px] font-mono text-ux-text-2 truncate max-w-[180px]"
                                title={eid}
                            >
                                {eid.replace(/^preset_env_/, "").replace(/_/g, " ")}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {threatIds.map((tid) => (
                        <tr key={tid}>
                            <th
                                className="text-left p-2 text-[12px] font-mono text-ux-text-2 align-top truncate max-w-[200px]"
                                title={tid}
                            >
                                {tid.replace(/^preset_threat_/, "").replace(/_/g, " ")}
                            </th>
                            {envIds.map((eid) => {
                                const cell = lookup.get(
                                    `${tid}|${eid}|${guidanceForRow}`,
                                );
                                if (!cell) {
                                    return (
                                        <td
                                            key={eid}
                                            className="p-2 align-top"
                                        >
                                            <div className="text-[11px] text-ux-text-3 italic">
                                                —
                                            </div>
                                        </td>
                                    );
                                }
                                return (
                                    <td
                                        key={eid}
                                        className="p-2 align-top min-w-[180px]"
                                    >
                                        <MatrixCell
                                            cell={cell}
                                            onClick={() => onCellClick(cell)}
                                        />
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function MatrixRunResultsPage() {
    const { matrixJobId } = useParams();
    const navigate = useNavigate();
    const [matrix, setMatrix] = useState(null);
    const [err, setErr] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [activeGuidance, setActiveGuidance] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const m = await sim.matrix.get(matrixJobId);
            setMatrix(m);
            setErr(null);
            return m;
        } catch (e) {
            setErr(
                formatApiError(e, "Failed to load matrix"),
            );
            return null;
        }
    }, [matrixJobId]);

    useEffect(() => {
        let cancelled = false;
        let timer;
        const tick = async () => {
            const m = await refresh();
            if (cancelled) return;
            if (!m || TERMINAL.has(m.state)) return;
            timer = setTimeout(tick, POLL_MS);
        };
        tick();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [refresh]);

    useEffect(() => {
        if (matrix && !activeGuidance) {
            const gids = matrix?.axes?.guidance_profile_ids || [];
            if (gids.length) setActiveGuidance(gids[0]);
        }
    }, [matrix, activeGuidance]);

    const onDelete = useCallback(async () => {
        setDeleting(true);
        try {
            await sim.matrix.remove(matrixJobId);
            toast.success("Matrix deleted");
            navigate("/run/history");
        } catch (e) {
            toast.error(
                formatApiError(e, "Delete failed"),
            );
        } finally {
            setDeleting(false);
            setConfirmDelete(false);
        }
    }, [matrixJobId, navigate]);

    const onCellClick = useCallback(
        (cell) => {
            if (cell.run_id) navigate(`/analyse/runs/${cell.run_id}`);
        },
        [navigate],
    );

    if (err) {
        return (
            <div className="space-y-4" data-testid="matrix-results-page">
                <PageHeader title="Matrix" subtitle={`Job ${matrixJobId}`} />
                <div className="rounded-md border border-ux-danger/40 bg-ux-danger-soft/40 p-4 text-ux-danger text-[13px]">
                    {String(err)}
                </div>
                <Button
                    variant="ghost"
                    onClick={() => navigate("/run/matrix")}
                    data-testid="matrix-results-back"
                >
                    <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to composer
                </Button>
            </div>
        );
    }
    if (!matrix) {
        return (
            <div
                className="flex items-center justify-center h-[40vh] text-ux-text-3 font-mono text-[13px]"
                data-testid="matrix-results-loading"
            >
                Loading matrix…
            </div>
        );
    }

    const cells = matrix.cells || [];
    const completed = cells.filter((c) => c.state === "complete").length;
    const failed = cells.filter((c) => c.state === "failed").length;
    const total = cells.length;
    const overall = matrix.state || "running";

    const csvUrl = sim.matrix.exportCsvUrl(matrixJobId);
    const pdfUrl = sim.matrix.exportPdfUrl(matrixJobId);

    const gids = matrix?.axes?.guidance_profile_ids || [];
    const showGuidanceTabs = gids.length > 1;

    return (
        <div className="space-y-6" data-testid="matrix-results-page">
            <PageHeader
                title="Matrix Results"
                subtitle={`Job ${matrixJobId} · ${total} cells · ${matrix.seeds_per_cell} seeds/cell · ${matrix.dynamics_backend}`}
                actions={
                    <div className="flex items-center gap-2">
                        <Link
                            to="/run/matrix"
                            data-testid="matrix-results-new"
                            className="inline-flex items-center gap-1 text-[12px] text-ux-text-3 hover:text-ux-text-1"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" /> New matrix
                        </Link>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={refresh}
                            data-testid="matrix-results-refresh"
                        >
                            <RefreshCw className="w-3.5 h-3.5 mr-1" />
                            Refresh
                        </Button>
                    </div>
                }
            />

            {/* Status banner */}
            <div
                className="rounded-xl border border-ux-border-1 bg-ux-bg-1 p-4 flex items-center justify-between gap-3 flex-wrap"
                data-testid="matrix-status-banner"
            >
                <div className="flex items-center gap-3 flex-wrap">
                    <StatusPill
                        status={
                            overall === "complete"
                                ? "succeeded"
                                : overall === "complete_with_failures"
                                  ? "cancelling"
                                  : "running"
                        }
                        testId="matrix-overall-state"
                    >
                        {overall.replace(/_/g, " ")}
                    </StatusPill>
                    <span className="text-[12px] font-mono text-ux-text-2">
                        {completed}/{total} complete
                        {failed > 0 ? ` · ${failed} failed` : ""}
                    </span>
                    {matrix.dynamics_backend === "jsbsim_hybrid" && (
                        <span className="text-[11px] font-mono text-ux-warning">
                            jsbsim_hybrid: approximate (only interceptor_a
                            calibrated)
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <a
                        href={csvUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="matrix-export-csv"
                        className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded border border-ux-border-1 hover:bg-ux-bg-2 text-ux-text-2"
                    >
                        <Download className="w-3.5 h-3.5" />
                        CSV
                    </a>
                    <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="matrix-export-pdf"
                        className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1 rounded border border-ux-border-1 hover:bg-ux-bg-2 text-ux-text-2"
                    >
                        <FileText className="w-3.5 h-3.5" />
                        PDF
                    </a>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmDelete(true)}
                        data-testid="matrix-delete-button"
                        className="text-ux-danger hover:bg-ux-danger-soft"
                    >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Delete matrix
                    </Button>
                </div>
            </div>

            {/* Grid (with guidance tabs if K > 1) */}
            {showGuidanceTabs ? (
                <Tabs
                    value={activeGuidance || gids[0]}
                    onValueChange={setActiveGuidance}
                >
                    <TabsList data-testid="matrix-guidance-tabs">
                        {gids.map((g) => (
                            <TabsTrigger
                                key={g}
                                value={g}
                                data-testid={`matrix-guidance-tab-${g}`}
                            >
                                {g
                                    .replace(/^preset_guidance_/, "")
                                    .replace(/_/g, " ")}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    {gids.map((g) => (
                        <TabsContent key={g} value={g}>
                            <MatrixGrid
                                matrix={matrix}
                                guidanceFilter={g}
                                onCellClick={onCellClick}
                            />
                        </TabsContent>
                    ))}
                </Tabs>
            ) : (
                <MatrixGrid
                    matrix={matrix}
                    guidanceFilter={gids[0]}
                    onCellClick={onCellClick}
                />
            )}

            <AlertDialog
                open={confirmDelete}
                onOpenChange={setConfirmDelete}
            >
                <AlertDialogContent
                    className="bg-ux-bg-1 border-ux-border-1"
                    data-testid="matrix-delete-dialog"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this matrix?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the matrix job, every cell scenario,
                            and every cell run. Saved scenarios in your
                            library are not affected.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            disabled={deleting}
                            data-testid="matrix-delete-confirm"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
